import { useState, useRef } from 'react';
import {
  getSaveCountinueFailedCount,
  getTimerFailedCount,
  resetAllFailureCounts
} from '../redux/reducers/api-call-state-reducer';
import { useDispatch, useSelector } from 'react-redux';
import { useContextEffect, useStableCallback } from './useBetterHooks';
import { appConfig } from '../constants';

const TIMER_INTERVAL_DURATION = 5_000;

export const useNetworkStatus = () => {
  // Soft signal for the Online/Offline badge — follows the browser immediately.
  const [isBrowserOnline, setIsBrowserOnline] = useState(navigator.onLine);
  // Hard signal for pausing the test / Connection Lost modal — only after write/timer API failures.
  const [isOnline, setIsOnline] = useState(true);
  const previousStatusRef = useRef(true);
  const dispatch = useDispatch();

  const timerFailedCount = useSelector(getTimerFailedCount);
  const saveCountinueFailedCount = useSelector(getSaveCountinueFailedCount);
  const statusIntervalId = useRef(null);

  const setOnlineState = useStableCallback((state) => {
    previousStatusRef.current = isOnline;
    setIsOnline(state);
  });

  useContextEffect({
    deps: [timerFailedCount, saveCountinueFailedCount],
    ctx: { isOnline },
    cb: () => {
      // Mark offline only after timer sync fails twice or a save/write fails once
      if (timerFailedCount >= 2 || saveCountinueFailedCount >= 1) {
        setOnlineState(false);
      } else if (timerFailedCount === 0 && saveCountinueFailedCount === 0) {
        setOnlineState(true);
      }
    }
  });

  const handleOffline = useStableCallback(() => {
    setIsBrowserOnline(false);
  });

  const handleOnline = useStableCallback(() => {
    setIsBrowserOnline(true);
    setOnlineState(true);
    clearInterval(statusIntervalId.current);
  });

  useContextEffect({
    deps: [],
    ctx: {},
    cb: () => {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }
  });

  const statusFetch = useStableCallback(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 4000);
    try {
      const response = await fetch(`${appConfig.apiBaseUrl}status`, {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const res = response.ok;
      if (res) {
        setOnlineState(true);
        clearInterval(statusIntervalId.current);
      }
    } catch (error) {
      clearTimeout(timeoutId);
      // Already offline — avoid re-setting offline and racing recovery
    }
  });

  useContextEffect({
    deps: [isOnline],
    ctx: {},
    cb: () => {
      if (!isOnline) {
        statusIntervalId.current = setInterval(statusFetch, TIMER_INTERVAL_DURATION);
      } else {
        clearInterval(statusIntervalId.current);
        dispatch(resetAllFailureCounts());
      }
    }
  });

  return {
    isOnline,
    isBrowserOnline,
    previousStatus: previousStatusRef.current
  };
};
